import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import React, { FC } from 'react'

import '@fontsource/space-grotesk'

import { LuneTheme } from '../theme'
import { useIsMobile } from '../utils'

import Button from './Button'
import Loading from './Loading'
import Modal from './Modal'

const CertificateModal: FC<{
    openModal: boolean
    onClose: () => void
    tilesRetired?: React.ReactNode
    tilesAllocated?: React.ReactNode
    registries: React.ReactNode
    certificate: React.ReactNode
    clientName: string
    loading?: boolean
    disableModal?: boolean
    /**
     * Short standalone empty-state message shown when there are no retired or
     * allocated tiles.
     *
     * This may be a short phrase or sentence. Defaults to
     * "No credits retired yet".
     */
    noCreditsRetiredLabel?: string
    /**
     * Short standalone section heading for retired credits tiles.
     *
     * This should usually be Title Case and omit trailing punctuation.
     * Defaults to "Retired".
     */
    retiredLabel?: string
    /**
     * Short standalone section heading for allocated (pending retirement)
     * credits tiles.
     *
     * This should usually be Title Case and omit trailing punctuation.
     * Defaults to "Allocated, pending retirement".
     */
    allocatedPendingRetirementLabel?: string
    /**
     * Short standalone section heading for registry badges.
     *
     * This should usually be Title Case and omit trailing punctuation.
     * Defaults to "Credits issued by".
     */
    creditsIssuedByLabel?: string
    /**
     * Footer text shown under the certificate details area.
     *
     * This may be a full sentence or multiple sentences. If omitted, the
     * component renders a default sentence including `clientName`.
     */
    footerText?: string
}> = ({
    onClose,
    openModal,
    certificate,
    tilesRetired,
    tilesAllocated,
    clientName,
    registries,
    loading,
    disableModal,
    noCreditsRetiredLabel = 'No credits retired yet',
    retiredLabel = 'Retired',
    allocatedPendingRetirementLabel = 'Allocated, pending retirement',
    creditsIssuedByLabel = 'Credits issued by',
    footerText,
}) => {
    const { palette } = LuneTheme

    const { isMobile } = useIsMobile()
    const resolvedFooterText =
        footerText ??
        `This Carbon Credit Certificate was issued and is guaranteed by Lune Climate Ltd. for ${clientName}.`

    const mobileSx = {
        borderRadius: '0',
        height: '100%',
        margin: 0,
        maxWidth: '100%',
        maxHeight: '100%',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    }

    const content = (
        <Box
            sx={{
                width: isMobile ? '100%' : '396px',
                padding: isMobile ? '24px 0' : '0',
                boxSizing: 'border-box',
            }}
        >
            {isMobile && (
                <Button
                    iconButton
                    leftIcon={<CloseIcon sx={{ width: '24px' }} />}
                    variant={'text'}
                    onClick={() => onClose()}
                    sx={{ mb: 2 }}
                />
            )}
            {loading ? (
                <Box
                    sx={{
                        height: '500px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Loading sx={{ height: '48px', width: '48px' }} />
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            maxWidth: '380px',
                            maxHeight: '380px',
                            margin: 'auto',
                            m: isMobile ? 1 : 'auto',
                            mt: 1,
                        }}
                    >
                        {certificate}
                    </Box>
                    <Box
                        sx={{
                            pl: 3,
                            pr: 3,
                            pb: 3,
                            pt: 2,
                            mb: 1,
                        }}
                    >
                        <Box
                            sx={{
                                '*': {
                                    fontFamily:
                                        'Space Grotesk, Arial, Helvetica, sans-serif !important',
                                },
                            }}
                        >
                            {!tilesRetired && !tilesAllocated ? (
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: '700',
                                    }}
                                >
                                    {noCreditsRetiredLabel}
                                </Typography>
                            ) : (
                                <>
                                    {tilesRetired && (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: '700',
                                                    mb: 1,
                                                }}
                                            >
                                                {retiredLabel}
                                            </Typography>
                                            <Box sx={{ mb: 2 }}>{tilesRetired}</Box>
                                        </>
                                    )}
                                    {tilesAllocated && (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: '700',
                                                    mb: 1,
                                                }}
                                            >
                                                {allocatedPendingRetirementLabel}
                                            </Typography>
                                            <Box sx={{ mb: 2 }}>{tilesAllocated}</Box>
                                        </>
                                    )}
                                    {registries && (
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: '700',
                                                }}
                                            >
                                                {creditsIssuedByLabel}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start',
                                                    flexWrap: 'nowrap',
                                                    overflow: 'auto',
                                                    margin: '0 -32px',
                                                    pl: 4,
                                                    marginTop: '12px',
                                                }}
                                            >
                                                {registries}
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                        <Box
                            sx={{
                                pt: 2,
                                mt: 2,
                                borderTop: `1px solid ${palette.Grey300}`,
                            }}
                        >
                            <Typography variant={'caption'}>{resolvedFooterText}</Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )

    return disableModal ? (
        content
    ) : (
        <Modal
            withoutSpacing={true}
            maxWidth={'xs'}
            open={openModal}
            onClose={onClose}
            sx={{
                '.MuiPaper-root': {
                    borderRadius: '32px',
                    overflow: 'hidden',
                    zIndex: 9,
                    '> .MuiBox-root': {
                        height: '100%',
                        display: 'block',
                        overflow: 'hidden',
                        overflowY: 'auto',
                    },
                    ...(isMobile ? mobileSx : ''),
                },
            }}
        >
            {content}
        </Modal>
    )
}

export default CertificateModal
